import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { api, formatApiError, formatDate } from "@/lib/api";
import { useLiveRefresh } from "@/lib/live";

const time = (s) => {
  const span = [s.start, s.end].filter(Boolean).join(" – ");
  const day = s.date ? formatDate(s.date) : "";
  if (day || span) return [day, span].filter(Boolean).join(" · ");
  return s.completed_at ? new Date(s.completed_at).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "";
};

const STATE = {
  done: { label: "Done", tone: "text-emerald-700" },
  in_progress: { label: "In progress", tone: "text-amber-700" },
  pending: { label: "Not started", tone: "text-neutral-400" },
};
const stageName = (s) => s.label.split(" — ")[0].replace(" (Med Pack Register)", "");
const count = (v) => (v === "" || v == null ? "" : Number(v).toLocaleString("en-IN"));


/** /s/:number?stage= — what a scanned sheet shows: the job (number, product code, customer, quantity), the material
 * issued (roll and lot numbers, required and issued metres), then each department's own sheet details up to the
 * one scanned (the sections come from the server, routes/scan_sheet.py). */
export default function ScanView() {
  const { number } = useParams();
  const [params] = useSearchParams();
  const stage = params.get("stage") || "";
  const [state, setState] = useState({ loading: true });

  const load = () => api.get(`/jobs/by-number/${encodeURIComponent(number)}`)
    .then(({ data: ref }) => api.get(`/jobs/${ref.id}/scan`, { params: { stage } }))
    .then(({ data }) => setState({ data }))
    .catch((e) => setState({ error: formatApiError(e) }));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [number, stage]);
  useLiveRefresh("jobs", load);

  const { data, error, loading } = state;
  const m = data?.material || {};
  const none = <span className="text-neutral-300">—</span>;

  return (
    <div className="min-h-screen bg-[#F7F7F8] p-3 md:p-6" data-testid="scan-view">
      <div className="max-w-4xl mx-auto space-y-3">
        {loading && <div className="label-caps p-4">Opening {number}…</div>}
        {error && <div className="border border-neutral-200 bg-white p-5 text-sm font-medium" data-testid="scan-error">{error}</div>}
        {data && (
          <>
            <section className="border border-neutral-200 bg-white p-4">
              <div className="label-caps">Job no.</div>
              <div className="font-display text-2xl font-bold font-mono-num" data-testid="scan-job-no">{data.job_number}</div>
              <div className="mt-2 label-caps">Product code</div>
              <div className="text-lg font-semibold font-mono-num" data-testid="scan-product-code">{data.product_code || "—"}</div>
              <div className="mt-2 grid grid-cols-2 gap-4">
                <div>
                  <div className="label-caps">Customer</div>
                  <div className="text-sm font-semibold" data-testid="scan-customer">{data.customer_name || "—"}</div>
                </div>
                <div>
                  <div className="label-caps">Quantity</div>
                  <div className="text-lg font-bold font-mono-num" data-testid="scan-quantity">
                    {data.quantity != null ? Number(data.quantity).toLocaleString("en-IN") : "—"} <span className="text-xs font-medium text-neutral-500">{data.unit || ""}</span>
                  </div>
                </div>
              </div>
              {data.product_name && <div className="mt-2 text-xs text-neutral-500" data-testid="scan-product-name">{data.product_name}</div>}
            </section>

            <section className="border border-neutral-200 bg-white" data-testid="scan-material">
              <div className="px-4 py-2 border-b border-neutral-200 label-caps">Material used</div>
              <table className="w-full text-sm"><tbody>
                {[["Material", m.item], ["Width (mm)", m.width], ["Required (m)", count(m.required_m)], ["Issued by Stores (m)", count(m.issued_m)], ["Rolls issued", count(m.rolls_issued)],
                  ["Roll no.", m.rolls], ["Lot no.", m.lot], ["GRN", m.grn], ["Issued by", m.issued_by]].map(([k, v]) => (
                  <tr key={k} className="border-t border-neutral-100 first:border-0">
                    <td className="px-4 py-2 text-neutral-500 w-1/3">{k}</td>
                    <td className="px-4 py-2 font-medium font-mono-num break-words">{v || none}</td>
                  </tr>
                ))}
              </tbody></table>
              {!m.rolls && <div className="px-4 pb-3 text-[11px] text-neutral-400">Roll numbers appear once Stores issues material against this job.</div>}
            </section>

            {/* One block per department, as that department's own sheet records it; the sheet scanned (and the one
                before it) open, the rest a tap away. */}
            <div className="space-y-2" data-testid="scan-stages">
              {data.stages.map((s, i) => {
                const idx = data.stages.findIndex((x) => x.key === stage);
                const open = !stage ? s.sections.length > 0 : s.key === stage || (idx > 0 && i === idx - 1);
                const summary = [s.operator && `${s.key === "stores_issue" ? "Issued by" : "Operator"}: ${s.operator}`, s.approved_by && `QC: ${s.approved_by}`, time(s)].filter(Boolean).join(" · ");
                return (
                  <details key={s.key} open={open} className="border border-neutral-200 bg-white group" data-testid={`scan-stage-${s.key}`}>
                    <summary className="cursor-pointer list-none px-4 py-3 flex flex-wrap items-center gap-x-3 gap-y-1">
                      <span className="font-semibold">{stageName(s)}</span>
                      <span className={`text-[10px] font-semibold uppercase ${STATE[s.state].tone}`}>{STATE[s.state].label}</span>
                      {s.key === stage && <span className="text-[10px] font-semibold uppercase text-[#FF5A00]">This sheet</span>}
                      {summary && <span className="w-full text-xs text-neutral-500 font-normal">{summary}</span>}
                    </summary>
                    {s.sections.length === 0 && <div className="px-4 pb-3 text-xs text-neutral-400">Nothing recorded at this stage yet.</div>}
                    {s.sections.map((sec) => (
                      <div key={sec.title} className="border-t border-neutral-100">
                        <div className="px-4 pt-2 label-caps text-[10px]">{sec.title}</div>
                        <table className="w-full text-sm"><tbody>
                          {sec.rows.map(([k, v]) => (
                            <tr key={k} className="border-t border-neutral-50 first:border-0 align-top">
                              <td className="px-4 py-1.5 text-neutral-500 w-1/2 md:w-2/5">{k}</td>
                              <td className="px-4 py-1.5 font-medium font-mono-num break-words">{v}</td>
                            </tr>
                          ))}
                        </tbody></table>
                      </div>
                    ))}
                  </details>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
